import type { ReactNode } from "react";
import { Link } from "wouter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { WidgetPanel } from "@/components/dashboard-grid/WidgetPanel";
import { TasksRail } from "@/components/dashboard/TasksRail";
import { RemindersRail } from "@/components/dashboard/RemindersRail";
import { SuggestionRow } from "@/components/origination/SuggestionRow";
import { useSuggestions } from "@/hooks/useSuggestions";
import type { OverviewAttn, OverviewDeal } from "@/hooks/useDashboardOverview";
import type { OriginationPayload } from "@shared/dashboardOrigination";

const shortDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

function Section({ title, count, children }: { title: string; count?: number; children: ReactNode }) {
  return (
    <section className="py-1">
      <h3 className="px-3 pt-1.5 pb-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title}{count !== undefined && <span className="ml-1 font-normal">{count}</span>}
      </h3>
      {children}
    </section>
  );
}

function Item({ kind, tone, href, title, sub, when }: { kind: string; tone?: "crit" | "good"; href: string | null; title: string; sub?: string; when?: ReactNode }) {
  const body = (
    <span className="flex items-baseline gap-2 px-3 py-1 text-[0.8125rem] hover:bg-muted/40">
      <span className={cn("shrink-0 w-[4.4rem] text-center text-[10px] font-semibold uppercase tracking-wide rounded px-1 py-px bg-muted text-muted-foreground",
        tone === "crit" && "bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300",
        tone === "good" && "bg-emerald-50 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300")}>{kind}</span>
      <span className="truncate flex-1 min-w-0">{title}{sub && <span className="ml-1 text-xs text-muted-foreground">{sub}</span>}</span>
      {when && <span className="shrink-0 text-xs text-muted-foreground">{when}</span>}
    </span>
  );
  return href ? <Link href={href} className="block">{body}</Link> : body;
}

/** Deal deadlines, inbound to triage, my tasks, reminders, and intro suggestions to confirm. */
export function TodayWidget({ attention, deals, inbound }: { attention: OverviewAttn[]; deals: OverviewDeal[]; inbound: OriginationPayload["inbound"] | undefined }) {
  const qc = useQueryClient();
  const projectToClient = new Map(deals.filter((d) => d.projectId).map((d) => [d.projectId!, d.client.id]));
  const hrefFor = (a: OverviewAttn) =>
    a.entity.type === "client" ? `/clients/${a.entity.id}`
      : a.entity.type === "project" && projectToClient.has(a.entity.id) ? `/clients/${projectToClient.get(a.entity.id)}`
      : null;
  const setMna = useMutation({
    mutationFn: (clientId: string) => apiRequest("PATCH", `/api/companies/${clientId}`, { engagementType: "M&A" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["/api/dashboard/overview"] }); toast.success("Engagement type set to M&A"); },
    onError: () => toast.error("Couldn't update engagement type"),
  });
  const suggestions = useSuggestions({});
  const pending = suggestions.data ?? [];

  return (
    <WidgetPanel title="Today" action={<Link href="/tasks" className="text-xs font-medium text-primary-text hover:underline">Tasks</Link>}>
      <Section title="Deal deadlines" count={attention.length}>
        {attention.length === 0 ? <p className="px-3 py-1 text-xs italic text-muted-foreground">Nothing due.</p> : attention.map((a, i) => (
          <div key={i} className="flex items-center">
            <div className="flex-1 min-w-0">
              <Item kind={a.kind === "hygiene" ? "Fix" : a.severity === "high" ? "Overdue" : "Due"} tone={a.severity === "high" ? "crit" : undefined}
                href={hrefFor(a)} title={a.title} />
            </div>
            {a.kind === "hygiene" && a.action?.setEngagementType === "M&A" && (
              <button type="button" onClick={() => setMna.mutate(a.entity.id)} disabled={setMna.isPending}
                className="mr-3 text-[11px] font-medium text-primary-text hover:underline disabled:opacity-60">Set M&A</button>
            )}
          </div>
        ))}
      </Section>
      <Section title="Inbound to triage" count={(inbound?.valuations.length ?? 0) + (inbound?.replies.length ?? 0)}>
        {inbound?.valuations.map((v) => (
          <Item key={v.id} kind="Valuation" tone="good" href="/website" title={v.companyName ?? v.contactName ?? "Valuation request"}
            sub={[v.contactName, v.headline].filter(Boolean).join(" · ")} when={shortDate(v.submittedAt)} />
        ))}
        {inbound?.replies.map((r) => (
          <Item key={r.id} kind="Reply" href="/mass-outreach" title={r.companyName ?? "Outreach reply"} sub={r.ownerName ?? undefined} when={shortDate(r.lastSentAt)} />
        ))}
        {!inbound?.valuations.length && !inbound?.replies.length && <p className="px-3 py-1 text-xs italic text-muted-foreground">Nothing waiting.</p>}
      </Section>
      <div className="px-2"><TasksRail /></div>
      <div className="px-2"><RemindersRail /></div>
      <Section title="Intro suggestions" count={pending.length}>
        {pending.length === 0
          ? <p className="px-3 py-1 text-xs italic text-muted-foreground">No intros waiting to be confirmed.</p>
          : pending.map((s) => (
            <div key={s.id} className="px-2">
              <SuggestionRow s={s} onAccept={() => suggestions.accept.mutate(s.id)} onDismiss={() => suggestions.dismiss.mutate(s.id)}
                pending={suggestions.accept.isPending || suggestions.dismiss.isPending} />
            </div>
          ))}
      </Section>
    </WidgetPanel>
  );
}
